import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Modal, Form, Button } from "semantic-ui-react";
import { ControlledContactDetails } from "../../../components/global_components/form_components/ControlledContactDetails";
import { Person } from "../../../queries/contacts";
import { buildUpdateContactPayload, UpdateContactFormData } from "../utils/buildContactPayload";
import { usePerson, useUpdatePerson } from "../hooks/usePerson";
import { ContactAvatar } from "../../permits/components/ContactAvatar";
import { useContactHistory } from "../hooks/useContactHistory";
import { PastContactRecordsModal } from "../../crm/components/PastContactRecordsModal";
import { compact } from "lodash";
import { ContactFieldType } from "../../../queries/crmTopics";
import { yupResolver } from "@hookform/resolvers";
import * as yup from "yup";

interface EditContactModalProps {
  open: boolean;
  onClose: () => void;
  personId: number | string;
  onSuccess?: (data: Person) => void;
  requiredFields?: ContactFieldType[];
}

export const EditContactModal: React.FC<EditContactModalProps> = ({
  open,
  onClose,
  personId,
  onSuccess,
  requiredFields,
}) => {
  const [isPastRecordsModalOpen, setIsPastRecordsModalOpen] = useState(false);

  const { data: person, refetch } = usePerson(personId);
  const { history } = useContactHistory(personId);
  const { updateContact, isLoading } = useUpdatePerson(personId);
  const isRequired = (field: ContactFieldType) => requiredFields?.includes(field);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<UpdateContactFormData>({
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      mailAddress: {
        address: "",
        address2: "",
        city: "",
        state: "",
        zip: "",
        countryId: "US",
      },
      homeAddress: {
        address: "",
        address2: "",
        city: "",
        state: "",
        zip: "",
        countryId: "US",
      },
      sameMailAndPhysicalAddress: false,
      licenseNumber: "",
      companyName: "",
    },
    resolver: yupResolver(
      yup.object().shape({
        firstName: isRequired("FirstName")
          ? yup.string().required("First name is required")
          : yup.string().nullable().optional(),
        lastName: isRequired("LastName")
          ? yup.string().required("Last name is required")
          : yup.string().nullable().optional(),
        email: isRequired("Email")
          ? yup.string().email("Invalid email address").required("Email is required")
          : yup.string().email("Invalid email address").nullable().optional(),
        phone: isRequired("Phone")
          ? yup.string().required("Phone number is required")
          : yup.string().nullable().optional(),
      }),
    ),
    shouldUnregister: false,
  });

  const firstName = watch("firstName");
  const lastName = watch("lastName");
  const fullName = compact([lastName, firstName]).join(", ");

  useEffect(() => {
    if (open && person) {
      reset({
        firstName: person.firstName || "",
        lastName: person.lastName || "",
        email: person.email || "",
        phone: person.phone || "",
        mailAddress: {
          address: person.address || "",
          address2: person.address2 || "",
          city: person.city || "",
          state: person.state || "",
          zip: person.zip || "",
          countryId: person.countryId || "US",
        },
        homeAddress: {
          address: person.homeAddress || "",
          address2: person.homeAddress2 || "",
          city: person.homeCity || "",
          state: person.homeState || "",
          zip: person.homeZip || "",
          countryId: person.homeCountryId || "US",
        },
        sameMailAndPhysicalAddress: person.sameMailAndPhysicalAddress || false,
        licenseNumber: person.licenseNumber || "",
        companyName: person.companyName || "",
      });
    }
  }, [open, person, reset]);

  const handleFormSubmit = async (data: UpdateContactFormData) => {
    const payload = buildUpdateContactPayload(data);
    await updateContact({
      ...payload,
      skipNameValidation: true,
    });
    const updatedPerson = await refetch();
    if (updatedPerson) onSuccess?.(updatedPerson);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      className='edit-contact-modal tw-h-[min(650px,90vh)] tw-w-[90%] md:tw-w-[480px]'
    >
      <PastContactRecordsModal
        open={isPastRecordsModalOpen}
        onClose={() => setIsPastRecordsModalOpen(false)}
        contactId={`${personId}`}
      />
      <div
        role='button'
        tabIndex={0}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}
        className='tw-flex tw-h-full tw-w-full tw-flex-col'
      >
        {/* Header */}
        <div className='tw-flex tw-flex-shrink-0 tw-justify-between tw-rounded-t tw-bg-primary-darker'>
          <div className='tw-ml-[16px] tw-translate-y-[50%] tw-rounded-full tw-bg-white tw-p-[2px]'>
            <ContactAvatar
              size='56px'
              fontScale='150%'
              firstName={person?.firstName || ""}
              lastName={person?.lastName || ""}
              aria-hidden='false'
            />
          </div>
          <button
            className='tw-utility-button tw-mx-[16px] tw-font-bold tw-leading-[24px] tw-tracking-[0.15px] tw-text-white'
            onClick={onClose}
          >
            Cancel
          </button>
        </div>

        {/* Content */}
        <div className='tw-flex tw-flex-grow tw-flex-col tw-overflow-auto sm:tw-overflow-visible'>
          <div className='tw-flex-shrink-0 tw-p-6 tw-pb-0 tw-pt-[36px]'>
            <div className='tw-mb-[16px] tw-flex tw-flex-shrink-0 tw-justify-between'>
              <span className='tw-flex-1 tw-text-[18px] tw-font-bold tw-leading-[160%]'>{fullName}</span>
              <div className='tw-flex tw-gap-[8px] tw-text-[14px] tw-font-bold tw-leading-[20px] tw-text-text-secondary'>
                <span className='tw-flex tw-h-fit tw-flex-shrink-0 tw-flex-nowrap tw-items-center tw-justify-center tw-gap-[4px] tw-rounded-[4px] tw-bg-grey-95 tw-px-[8px] tw-py-[4px] tw-align-middle'>
                  {person?.personType === "citizen" ? "Resident" : "Employee"}
                </span>
                {history?.length ? (
                  <div
                    role='button'
                    tabIndex={0}
                    className='tw-flex tw-h-fit tw-flex-shrink-0 tw-cursor-pointer tw-flex-nowrap tw-items-center tw-justify-center tw-gap-[4px] tw-rounded-[4px] tw-bg-grey-95 tw-px-[8px] tw-py-[4px] tw-align-middle hover:tw-text-primary-dark'
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setIsPastRecordsModalOpen(true);
                    }}
                  >
                    {history.length} record{history.length !== 1 ? "s" : ""}
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          <div className='tw-min-h-0 tw-flex-grow tw-overflow-auto tw-px-6 sm:tw-overflow-visible'>
            <Form onSubmit={handleSubmit(handleFormSubmit)} className=''>
              <ControlledContactDetails
                control={control}
                errors={errors}
                addressFieldName='mailAddress'
                wrap
                requiredFields={requiredFields}
              />
            </Form>
          </div>
        </div>

        {/* Actions */}
        <div className='tw-flex tw-flex-shrink-0 tw-justify-end tw-gap-3 tw-rounded-b tw-border-x-0 tw-border-b-0 tw-border-t tw-border-solid tw-border-black tw-border-opacity-10 tw-bg-white tw-p-3 tw-text-right'>
          <Button onClick={handleSubmit(handleFormSubmit)} primary loading={isLoading} className='tw-flex-grow'>
            Save Profile
          </Button>
        </div>
      </div>
    </Modal>
  );
};
